import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  Paper,
  CircularProgress,
} from '@mui/material';
import { Header } from 'components/home';
import { ContainerForm } from 'components/shared/container_form';
import { TopNavBar } from 'components/top_nav_bar';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useNavigate, useParams } from 'react-router-dom';
import { CuffaAPI } from 'api/cuffa/cuffa.api';
import { useCuffaStore } from 'stores/cuffa/cuffa.store';
import { ROUTES } from 'routes/constants';

// Mapeamento dos meses da API para exibição
const MES_MAP_VIEW: { [key: string]: string } = {
  january: 'Jan', february: 'Fev', march: 'Mar', april: 'Abr', may: 'Mai', june: 'Jun',
  july: 'Jul', august: 'Ago', september: 'Set', october: 'Out', november: 'Nov', december: 'Dez'
};

// Mapeamento das unidades de medida para nomes legíveis
const unitNameMap: Record<string, string> = {
  QUILOGRAMAS: 'KG',
  TONELADAS: 'T',
  LITROS: 'L',
  UNIDADES: 'Un',
  CABECAS: 'Cabeças',
  'MIL FRUTOS': 'Mil Frutos',
  ARROBAS: 'Arrobas',
  MACOS: 'Maços',
  FARDOS: 'Fardos',
  CAIXAS: 'Caixas',
  'MIL LITROS': 'Mil Litros',
};

// Interface para os dados de um único produto processado
interface IProdutoProcessado {
  id: string;
  nome: string;
  origem: 'animal' | 'vegetal';
  volumeProduzido: number | null;
  unidadeVolume: string;
  volumeComercializado: number | null;
  valorComercializado: number | null;
  volumeConsumido: number | null;
  volumePerdido: number | null;
  mesesDisponibilidade: {
    anoTodo: boolean;
    meses: string[];
  };
}

// Interface para os dados completos da tela
interface IProdutosProcessadosData {
  produzProcessados: boolean;
  produtos: IProdutoProcessado[];
}

// Função auxiliar para formatar valores monetários
const formatCurrency = (value: number | null | undefined) => {
  if (value === null || value === undefined) return 'Não informado';
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

// Função auxiliar para formatar números
const formatNumber = (value: number | null | undefined) => {
  if (value === null || value === undefined) return 'Não informado';
  return value.toLocaleString('pt-BR');
};


export const ProdutosProcessadosVisualizar: React.FC = () => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();

  const dadosParciais = useCuffaStore(state => state.dadosParciais);

  const [dados, setDados] = useState<IProdutosProcessadosData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!uuid) {
        setLoading(false);
        return;
      }

      try {
        const response = await CuffaAPI.getDataByID(uuid);

        if (response && response.dados) {
          const apiData = response.dados;

          const mappedData: IProdutosProcessadosData = {
            produzProcessados: apiData.family_produces_processed_products || false,

            // --- INÍCIO DA CORREÇÃO ---
            produtos: (apiData.products || [])
              .filter((p: any) => p.management_type === null) // Filtra apenas produtos processados
              .map((p: any): IProdutoProcessado => {         // Mapeia com os nomes de campo corretos

                // Lógica para tratar os meses, que pode vir como string ou objeto
                let availabilityMonths = p.available_months;
                if (typeof availabilityMonths === 'string') {
                  try {
                    availabilityMonths = JSON.parse(availabilityMonths);
                  } catch (e) {
                    availabilityMonths = {}; // Fallback em caso de erro
                    console.error("Erro ao parsear available_months:", e);

                  }
                }

                const mesesSelecionados: string[] = [];
                if (availabilityMonths && !availabilityMonths.available_year_round) {
                  for (const [key, value] of Object.entries(availabilityMonths)) {
                    if (value === true && MES_MAP_VIEW[key]) {
                      mesesSelecionados.push(MES_MAP_VIEW[key]);
                    }
                  }
                }

                return {
                  id: p.uuid,
                  nome: p.custom_product_name,
                  origem: (p.production_type || '').toLowerCase(),
                  volumeProduzido: parseFloat(p.volume_produced) || null,
                  unidadeVolume: p.measurement_unit,
                  volumeComercializado: parseFloat(p.volume_commercialized) || null,
                  valorComercializado: parseFloat(p.commercialized_value) || null,
                  volumeConsumido: parseFloat(p.volume_consumed_donated) || null,
                  mesesDisponibilidade: {
                    anoTodo: availabilityMonths?.available_year_round || false,
                    meses: mesesSelecionados,
                  },
                  volumePerdido: parseFloat(p.volume_lost) || null,
                };
              }),
          };
          try {
            const saved = dadosParciais?.produtosProcessados;
            if (saved) {
              const localMapped: IProdutosProcessadosData = {
                produzProcessados: saved.possui === 'sim',
                produtos: (saved.produtos || []).map((p: any) => ({
                  id: p.id,
                  nome: p.nome,
                  origem: p.origem,
                  volumeProduzido: p.volumeProduzido ?? null,
                  unidadeVolume: p.unidadeVolume ?? '',
                  volumeComercializado: p.volumeComercializado ?? null,
                  valorComercializado: p.valorComercializado ?? null,
                  volumeConsumido: p.volumeConsumido ?? null,
                  volumePerdido: p.volumePerdido ?? null,
                  mesesDisponibilidade: {
                    anoTodo: !!p.mesesDisponibilidade?.anoTodo,
                    meses: (p.mesesDisponibilidade?.meses || []).map((m: string) =>
                      m && m.length > 0 ? (m.charAt(0).toUpperCase() + m.slice(1)) : m
                    )
                  }
                }))
              };
              setDados(localMapped);
            } else {
              setDados(mappedData);
            }
          } catch (e) {
            console.error('Erro ao aplicar dados locais de produtos processados:', e);
            setDados(mappedData);
          }
        } else {
          setDados(null);
          console.error("Dados de produtos processados não encontrados na resposta da API:", response);

        }
      } catch (error) {
        console.error("Erro ao buscar dados de produtos processados:", error);
        setDados(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [uuid]);

  if (loading) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Produtos Processados" showBackButton />
        <Box width="100%" display="flex" justifyContent="center" alignItems="center" height="50vh">
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
        </Box>
      </MenuProvider>
    );
  }

  if (!dados) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Produtos Processados" showBackButton />
        <Box width="100%" display="flex" justifyContent="center" margin="2rem 0">
          <Typography>Não foi possível carregar os dados. Verifique o console para mais detalhes.</Typography>
        </Box>
      </MenuProvider>
    );
  }

  const produtosAnimais = dados.produtos.filter(p => p.origem === 'animal');
  const produtosVegetais = dados.produtos.filter(p => p.origem === 'vegetal');

  return (
    <MenuProvider>
      <TopNavBar />
      <Header title="Visualização de Produtos Processados" showBackButton />
      <Box width="100%" display="flex" justifyContent="center" margin="2rem">
        <Box sx={{ maxWidth: '1152px', padding: '2rem', width: '100%' }}>
          <ContainerForm title="Produção de Itens Processados" width="100%">
            <Paper elevation={0} sx={{ p: 3, mb: 4, border: '2px solid #000', borderRadius: '8px' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                A unidade familiar produz itens processados ou beneficiados?
              </Typography>
              <Typography variant="body1">
                {dados.produzProcessados ? 'Sim' : 'Não'}
              </Typography>
            </Paper>

            {dados.produzProcessados && (
              <>
                {produtosAnimais.length > 0 && (
                  <Box mb={4}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Produtos de Origem Animal
                    </Typography>
                    {produtosAnimais.map(produto => (
                      <DetalhesProduto key={produto.id} produto={produto} />
                    ))}
                  </Box>
                )}

                {produtosVegetais.length > 0 && (
                  <Box>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Produtos de Origem Vegetal
                    </Typography>
                    {produtosVegetais.map(produto => (
                      <DetalhesProduto key={produto.id} produto={produto} />
                    ))}
                  </Box>
                )}

                {dados.produtos.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum produto processado foi registrado.
                  </Typography>
                )}
              </>
            )}

            <Box marginTop="2rem" display="flex" justifyContent="center" gap="1rem">
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate(ROUTES.DETALHES_PRODUCAO_VISUALIZAR.replace(':uuid', uuid ?? ''))}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  if (uuid) {
                    navigate(ROUTES.INSPECAO_PRODUTOS_CUFFA_VISUALIZAR.replace(':uuid', uuid));
                  }
                }}
              >
                Avançar
              </Button>
            </Box>
          </ContainerForm>
        </Box>
      </Box>
    </MenuProvider>
  );
};

// Subcomponente para exibir os detalhes de cada produto
const DetalhesProduto: React.FC<{ produto: IProdutoProcessado }> = ({ produto }) => (
  <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: '8px' }}>
    <Typography variant="h6" fontWeight="bold">
      {produto.nome}
    </Typography>
    <Divider sx={{ mb: 2 }} />
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" fontWeight="bold">Volume Produzido (último ano):</Typography>
        <Typography variant="body1">{formatNumber(produto.volumeProduzido) || 'Não informado'} {unitNameMap[produto.unidadeVolume] || produto.unidadeVolume}</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" fontWeight="bold">Volume Comercializado (último ano):</Typography>
        <Typography variant="body1">{formatNumber(produto.volumeComercializado) || 'Não informado'} {unitNameMap[produto.unidadeVolume] || produto.unidadeVolume}</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" fontWeight="bold">Valor Comercializado (R$):</Typography>
        <Typography variant="body1">{formatCurrency(produto.valorComercializado)}</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" fontWeight="bold">Volume Consumido/Doado/Trocado:</Typography>
        <Typography variant="body1">{formatNumber(produto.volumeConsumido) || 'Não informado'} {unitNameMap[produto.unidadeVolume] || produto.unidadeVolume}</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" fontWeight="bold">Volume Perdido:</Typography>
        <Typography variant="body1">{formatNumber(produto.volumePerdido) || 'Não informado'} {unitNameMap[produto.unidadeVolume] || produto.unidadeVolume}</Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="subtitle2" fontWeight="bold">Meses de Disponibilidade:</Typography>
        {produto.mesesDisponibilidade.anoTodo ? (
          <Chip label="Disponível o Ano Todo" color="success" size="small" />
        ) : (
          <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
            {produto.mesesDisponibilidade.meses.length > 0 ? (
              produto.mesesDisponibilidade.meses.map(mes => <Chip key={mes} label={mes} size="small" />)
            ) : (
              <Typography variant="body2" color="text.secondary">Nenhum mês especificado.</Typography>
            )}
          </Box>
        )}
      </Grid>
    </Grid>
  </Paper>
);

export default ProdutosProcessadosVisualizar;
